import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { groupBy } from 'lodash';
import { selectIsDemoMode, selectDemoOffers } from './demo';

// Base offers selector
const selectBaseOffers = (state: RootState) => state.offers.offers;

// Enhanced selector that includes demo data when in demo mode
export const selectOffers = createSelector(
  [selectBaseOffers, selectIsDemoMode, selectDemoOffers],
  (baseOffers, isDemoMode, demoOffers) => {
    if (!isDemoMode) return baseOffers;
    // Put demo offers first
    return [...demoOffers, ...baseOffers];
  }
);

export const selectBuyingOffers = createSelector([selectOffers], (offers) =>
  offers.filter((offer) => offer.listings.type === 'BUYER')
);

export const selectSellingOffers = createSelector([selectOffers], (offers) =>
  offers.filter((offer) => offer.listings.type === 'SELLER')
);

export const selectActiveSellingOffers = createSelector(
  [selectSellingOffers],
  (offers) =>
    offers.filter(
      (offer) => offer.status === 'PENDING' || offer.status === 'ACCEPTED' || offer.status === 'COUNTERED'
    )
);

export const selectRescindedOffers = createSelector([selectOffers], (offers) =>
  offers.filter((offer) => offer.status === 'RESCINDED')
);

export const selectPendingBuyingOffers = createSelector(
  [selectBuyingOffers],
  (offers) => offers.filter((offer) => offer.status === 'REVIEW')
);

export const selectPendingSellingOffers = createSelector(
  [selectSellingOffers],
  (offers) => offers.filter((offer) => offer.status === 'PENDING')
);

export const selectOffersGroupedByBuyer = createSelector(
  [selectSellingOffers],
  (offers) => {
    return groupBy(offers, 'userId');
  }
);

// Select active offers for sellers (PENDING, REVIEW, ACCEPTED, or COUNTERED)
export const selectActiveOffersForSeller = createSelector(
  [selectSellingOffers],
  (offers) =>
    offers.filter(
      (offer) =>
        offer.status === 'PENDING' ||
        offer.status === 'REVIEW' ||
        offer.status === 'ACCEPTED' ||
        offer.status === 'COUNTERED'
    )
);

// Select submitted offers for buyers
export const selectSubmittedBuyingOffers = createSelector(
  [selectBuyingOffers],
  (offers) =>
    offers.filter(
      (offer) =>
        offer.status === 'PENDING' ||
        offer.status === 'REVIEW' ||
        offer.status === 'ACCEPTED'
    )
);

/**
 * How far along an offer is, most advanced first. Mirrors OFFER_PRECEDENCE in
 * the API's `agents/entity-resolver.ts`, which learned this the hard way:
 * recency alone is the wrong tie-break, because a closed or superseded offer
 * created after the live one sorts above the deal that is actually happening.
 *
 * Statuses absent here — DECLINED, RESCINDED — fall to the `?? 99` default and
 * land last, which is the point. They stay in the list so offer history and
 * the timeline still show them; they just stop being picked as the offer the
 * page is about.
 */
const OFFER_STAGE_PRECEDENCE: Record<string, number> = {
  ACCEPTED: 0,
  SIGNED: 1,
  REVIEWED: 2,
  REVIEW: 3,
  COUNTERED: 4,
  PENDING: 5,
};

// User-context-aware selector for offers by listingId and userId (works for all user types)
//
// Consumers take `[0]` as the offer the page is about (Detail.tsx uses it for
// the offer card, the buyer step flow, offerId and offerStatus), so the order
// here decides which offer a listing appears to have. Sorting by createdAt
// alone put a RESCINDED offer created after an ACCEPTED one at the front, and
// the admin listing page showed the rescinded one as the deal.
//
// Stage precedence is only safe because this is already scoped to one listing
// and one user. Across listings it inverts — an accepted offer stays accepted
// long after closing and would permanently outrank a newer offer on a
// different home — which is why no unscoped selector should copy this.
export const selectOffersByListingIdAndUserId = (
  listingId: string,
  userId: string
) =>
  createSelector([selectOffers], (offers) =>
    offers
      .filter((offer) => offer.listingId === listingId && offer.userId === userId)
      .sort((a, b) => {
        const byStage =
          (OFFER_STAGE_PRECEDENCE[a.status] ?? 99) -
          (OFFER_STAGE_PRECEDENCE[b.status] ?? 99);
        if (byStage !== 0) return byStage;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })
  );
